import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { getPublicBio } from "@/lib/bio.functions";
import { MiniSiteRenderer } from "@/components/mini-site/MiniSiteRenderer";
import type { MiniSiteTemplateId } from "@/lib/mini-site-templates";
import { MINI_SITE_TEMPLATES } from "@/lib/mini-site-templates";

const TEMPLATE_IDS = MINI_SITE_TEMPLATES.map((t) => t.id) as [MiniSiteTemplateId, ...MiniSiteTemplateId[]];

const searchSchema = z.object({
  t: z.enum(["light", "dark"]).optional().catch("dark"),
  preview_template: z.enum(TEMPLATE_IDS).optional().catch(undefined),
});

const RESERVED = new Set([
  "api","auth","chat","collection","email","explore","feed","leaderboard",
  "lovable","marketplace","me","profile","search","settings","unsubscribe",
  "wishlist","album","admin","www","app","about","help","support","privacy",
  "terms","login","signup","register","logout","link-bio","mini-site",
]);

export const Route = createFileRoute("/$username")({
  validateSearch: searchSchema,
  head: ({ params }) => ({
    meta: [
      { title: `@${params.username} — coleção de vinis` },
      { name: "description", content: `Confira a coleção de vinis de @${params.username} no viny.club` },
      { property: "og:title", content: `@${params.username} no viny.club` },
      { property: "og:description", content: "Coleção pública de vinis." },
       { property: "og:type", content: "profile" },
       { name: "twitter:card", content: "summary" },
    ],
  }),
  beforeLoad: ({ params }): never => {
    if (RESERVED.has(params.username.toLowerCase())) throw notFound();
    return undefined as never;
  },
  component: PublicBioPage,
  notFoundComponent: () => (
    <div className="grid min-h-screen place-items-center bg-black text-white">
      <div className="text-center">
        <h1 className="font-display text-3xl font-bold">Perfil não encontrado</h1>
        <Link to="/" className="mt-4 inline-block text-sm text-primary underline">voltar ao viny.club</Link>
      </div>
    </div>
  ),
});

function PublicBioPage() {
  const { username } = Route.useParams();
  const { t, preview_template } = Route.useSearch();
  const theme = (t ?? "dark") as "light" | "dark";

  const fetchBio = useServerFn(getPublicBio);
  const q = useQuery({
    queryKey: ["public-bio", username],
    queryFn: () => fetchBio({ data: { username } }),
  });

  if (q.isLoading) {
    return (
      <div className="grid min-h-screen place-items-center bg-black text-white">
        <div className="text-sm text-white/60">Carregando…</div>
      </div>
    );
  }
  if (!q.data) throw notFound();

  // Allow live preview of a different template via ?preview_template=
  const config = preview_template
    ? { ...q.data.mini_site, template_id: preview_template }
    : q.data.mini_site;

  return <MiniSiteRenderer bio={q.data} config={config} theme={theme} />;
}
